import React, { useState, useEffect } from "react";
import LearningLibraryModal from "./LearningLibraryModal";

// Goal type colors
const GOAL_TYPE_COLORS = {
  action: '#3b82f6',
  skill: '#10b981',
  attitude: '#8b5cf6',
};

// Chevron icon for accordion
const ChevronDown = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Individual Goal Row Component - renders a single table row for a goal
function GoalRow({ goalEntry, onSave, isSaving, onOpenModal }) {
  const [saveStatus, setSaveStatus] = useState(null);
  const [localEntry, setLocalEntry] = useState({
    onTheJobApplication: goalEntry?.onTheJobApplication || "",
    mentoringPeerSupport: goalEntry?.mentoringPeerSupport || "",
    formalLearning: Array.isArray(goalEntry?.formalLearning) ? goalEntry.formalLearning : [],
  });

  // Check if there are unsaved changes
  const hasChanges =
    localEntry.onTheJobApplication !== (goalEntry?.onTheJobApplication || "") ||
    localEntry.mentoringPeerSupport !== (goalEntry?.mentoringPeerSupport || "");

  // Sync text fields when goalEntry prop changes
  useEffect(() => {
    setLocalEntry(prev => ({
      ...prev,
      onTheJobApplication: goalEntry?.onTheJobApplication || "",
      mentoringPeerSupport: goalEntry?.mentoringPeerSupport || "",
    }));
  }, [goalEntry?.onTheJobApplication, goalEntry?.mentoringPeerSupport]);

  // Sync formalLearning when goalEntry changes
  useEffect(() => {
    setLocalEntry(prev => ({
      ...prev,
      formalLearning: Array.isArray(goalEntry?.formalLearning) ? goalEntry.formalLearning : [],
    }));
  }, [goalEntry?.entryIndex, goalEntry?.formalLearning]);

  // Clear "saved" status after 2 seconds
  useEffect(() => {
    if (saveStatus === 'saved') {
      const timer = setTimeout(() => setSaveStatus(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  const handleFieldChange = (field, value) => {
    setLocalEntry((prev) => ({ ...prev, [field]: value }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaveStatus('saving');
    try {
      await onSave(goalEntry.entryIndex, {
        onTheJobApplication: localEntry.onTheJobApplication,
        mentoringPeerSupport: localEntry.mentoringPeerSupport,
      });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus(null);
      alert("Failed to save. Please try again.");
    }
  };

  // Remove a specific resource
  const handleRemoveResource = (resourceId, resourceTitle) => {
    setLocalEntry((prev) => ({
      ...prev,
      formalLearning: prev.formalLearning.filter(r =>
        resourceId ? r.resourceId !== resourceId : r.resourceTitle !== resourceTitle
      ),
    }));

    if (resourceId) {
      onSave(goalEntry.entryIndex, {
        formalLearning: { action: 'remove', resourceId }
      });
    }
  };

  // Get suggested resources that haven't been added yet
  const suggestedResources = goalEntry.suggestedResources || [];
  const addedIdentifiers = localEntry.formalLearning.map(r =>
    r.resourceId ? String(r.resourceId) : r.resourceTitle
  );
  const availableResources = suggestedResources.filter(
    r => !addedIdentifiers.includes(r.resourceId ? String(r.resourceId) : r.resourceTitle)
  );

  // Add suggested resource from recommendation drawer
  const handleAddSuggestedResource = (resource) => {
    const newResource = {
      resourceId: resource.resourceId || null,
      resourceTitle: resource.resourceTitle,
      resourceType: resource.resourceType,
      resourceUrl: resource.resourceUrl || "",
    };
    setLocalEntry((prev) => ({
      ...prev,
      formalLearning: [...prev.formalLearning, newResource],
    }));

    if (resource.resourceId) {
      onSave(goalEntry.entryIndex, {
        formalLearning: { action: 'add', resourceId: resource.resourceId }
      });
    }
  };

  return (
    <>
      {/* Goal Data Row */}
      <tr className="data-row">
        <td className="goal-col">
          <div className="goal-cell">
            <div className="goal-cell-content">
              {/* Goal Type Badges */}
              <div className="goal-type-badges">
                {(goalEntry.goalTypes && goalEntry.goalTypes.length > 0) ? (
                  goalEntry.goalTypes.map((type) => (
                    <span
                      key={type}
                      className="goal-type-badge"
                      style={{ backgroundColor: GOAL_TYPE_COLORS[type] || '#6b7280' }}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </span>
                  ))
                ) : (
                  <strong className="goal-label">Goal {goalEntry.goalIndex + 1}:</strong>
                )}
              </div>
              {/* Goal Description */}
              <span className="goal-text">
                {goalEntry.goalDescription || goalEntry.goalName || goalEntry.goalText}
              </span>
            </div>
          </div>
        </td>
        <td className="data-col">
          <textarea
            className="entry-textarea"
            placeholder="Describe your experience-based assignment..."
            value={localEntry.onTheJobApplication}
            onChange={(e) => handleFieldChange("onTheJobApplication", e.target.value)}
            rows={4}
          />
        </td>
        <td className="data-col">
          <textarea
            className="entry-textarea"
            placeholder="Describe your monitoring and coaching activities..."
            value={localEntry.mentoringPeerSupport}
            onChange={(e) => handleFieldChange("mentoringPeerSupport", e.target.value)}
            rows={4}
          />
        </td>
        <td className="data-col formal-learning-col">
          <div className="formal-learning-content">
            <div className="resource-list">
              {localEntry.formalLearning.map((resource, idx) => (
                <div key={resource.resourceId || `${resource.resourceTitle}-${idx}`} className="resource-item">
                  <span className="resource-type">{resource.resourceType}:</span>
                  <a
                    href={resource.resourceUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-link"
                    onClick={(e) => !resource.resourceUrl && e.preventDefault()}
                  >
                    {resource.resourceTitle}
                  </a>
                  <button
                    className="remove-resource-btn"
                    onClick={() => handleRemoveResource(resource.resourceId, resource.resourceTitle)}
                    type="button"
                    title="Remove resource"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              className="browse-btn"
              onClick={() => onOpenModal(goalEntry.entryIndex)}
              type="button"
            >
              Browse Library →
            </button>
          </div>
        </td>
      </tr>

      {/* Recommendation Drawer Row - spans all columns */}
      {availableResources.length > 0 && (
        <tr className="recommendation-row">
          <td colSpan="4" className="recommendation-cell">
            <div className="recommendation-drawer-inline">
              <h4 className="rec-drawer-title">Recommended Resources</h4>
              <div className="recs-grid">
                {availableResources.map((resource, idx) => (
                  <div key={resource.resourceId || `${resource.resourceTitle}-${idx}`} className="rec-card">
                    <div className="rec-type-header">
                      <span className="rec-type-badge">{resource.resourceType || 'Resource'}</span>
                    </div>
                    <div className="rec-content">
                      <h3>{resource.resourceTitle}</h3>
                    </div>
                    <button
                      type="button"
                      className="begin-btn"
                      onClick={() => handleAddSuggestedResource(resource)}
                    >
                      Add to Formal Training →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Row-level save indicator */}
      {hasChanges && (
        <tr className="save-indicator-row">
          <td colSpan="4">
            <div className="row-save-section">
              <span className="unsaved-text">This goal has unsaved changes</span>
              <button
                className="save-btn-small"
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                type="button"
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </button>
              {saveStatus === 'saved' && <span className="save-status saved">✓</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Main CompetencyTable component - accordion with single table containing multiple goal rows
export default function CompetencyTable({
  competencyName,
  competencyIndex,
  goalEntries = [],
  onSave,
  isSaving,
}) {
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeEntryIndex, setActiveEntryIndex] = useState(null);

  const handleOpenModal = (entryIndex) => {
    setActiveEntryIndex(entryIndex);
    setModalOpen(true);
  };

  const handleResourceSelect = (resource) => {
    if (activeEntryIndex !== null) {
      onSave(activeEntryIndex, {
        formalLearning: { action: 'add', resourceId: resource._id }
      });
    }
    // Modal stays open so user can add more resources
  };

  return (
    <div className="dev-accordion-item">
      <button
        className={`dev-accordion-trigger ${expanded ? "expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <span className="dev-accordion-title">{competencyName}</span>
        <div className="dev-accordion-right">
          <span className="goal-count-badge">{goalEntries.length} goal{goalEntries.length !== 1 ? 's' : ''}</span>
          <ChevronDown className={`dev-accordion-chevron ${expanded ? "rotated" : ""}`} />
        </div>
      </button>

      {expanded && (
        <div className="dev-accordion-content">
          <div className="table-wrap">
            <table className="plan-table">
              <thead>
                <tr className="lead-dev-first">
                  <th>Competency & Development Goal</th>
                  <th
                    className="tooltip-header"
                    data-tooltip="Put new skills into practice through real work. This is where your learning becomes behavior change."
                  >
                    Experience-Based Assignment <span className="info-icon">i</span>
                    <span className="column-percentage">70% of your development</span>
                  </th>
                  <th
                    className="tooltip-header"
                    data-tooltip="Support and feedback from your leaders or peers to help you grow with guidance and accountability."
                  >
                    Monitoring, Coaching & Professional Learning <span className="info-icon">i</span>
                    <span className="column-percentage">20% of your development</span>
                  </th>
                  <th
                    className="tooltip-header"
                    data-tooltip="Your tailored learning experiences to learn new or reinforce core concepts and skills."
                  >
                    Formal Training <span className="info-icon">i</span>
                    <span className="column-percentage">10% of your development</span>
                  </th>
                </tr>
              </thead>
              <tbody>

                {/* Render each goal as its own row */}
                {goalEntries.map((goalEntry) => (
                  <GoalRow
                    key={`goal-${goalEntry.entryIndex}`}
                    goalEntry={goalEntry}
                    onSave={onSave}
                    isSaving={isSaving}
                    onOpenModal={handleOpenModal}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <LearningLibraryModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setActiveEntryIndex(null);
        }}
        onSelect={handleResourceSelect}
      />
    </div>
  );
}
